import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

const GOLD     = "#C9A96E";
const INK      = "#1C1915";
const INK_SOFT = "#4A4540";
const BORDER   = "#E7E1D8";
const SOFT_BG  = "#F7F5F2";
const WHITE    = "#FFFFFF";
const GREEN    = "#1A7A44";
const RED      = "#C0392B";

const inputStyle = {
  width: "100%", padding: "9px 11px",
  border: `1px solid ${BORDER}`, borderRadius: 7,
  fontSize: 13, color: INK, background: "#FAFAF8",
  boxSizing: "border-box", outline: "none",
};
const labelStyle = {
  display: "block", fontSize: 10, letterSpacing: "0.12em",
  textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600,
};
const sectionTitle = {
  fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase",
  color: GOLD, marginBottom: 14, fontWeight: 700,
};
const card = {
  background: WHITE, border: `1px solid ${BORDER}`,
  borderRadius: 12, padding: "22px 24px", marginBottom: 16,
};

function formatMoney(v) {
  return Number(v || 0).toLocaleString("fr-FR") + " MAD";
}
function toN(v) { return Number(v || 0); }

export default function ProjectSimulationPage({ cabinetId = null }) {
  const [prospects,    setProspects]    = useState([]);
  const [prospectId,   setProspectId]   = useState("");
  const [nomProjet,    setNomProjet]    = useState("");
  const [nomClient,    setNomClient]    = useState("");
  const [typeProjet,   setTypeProjet]   = useState("");
  const [zone,         setZone]         = useState("");
  const [typeSimulation, setTypeSimulation] = useState("vente");
  const [pays,         setPays]         = useState("Maroc");
  const [agenceIncluse, setAgenceIncluse] = useState("non");

  const [coutTerrain,      setCoutTerrain]      = useState("");
  const [coutConstruction, setCoutConstruction] = useState("");
  const [honorairesYoma,   setHonorairesYoma]   = useState("");
  const [chiffreAffaires,  setChiffreAffaires]  = useState("");

  useEffect(() => {
    if (cabinetId == null) return;
    supabase.from("prospects")
      .select("id, prenom, nom, type_projet, zone_souhaitee, budget_min, budget_max")
      .eq("cabinet_id", cabinetId)
      .order("created_at", { ascending: false })
      .then(({ data }) => { if (data) setProspects(data); });
  }, [cabinetId]);

  function handleSelectProspect(id) {
    setProspectId(id);
    if (!id) return;
    const p = prospects.find(p => p.id === id);
    if (!p) return;
    setNomClient(`${p.prenom} ${p.nom}`);
    setTypeProjet(p.type_projet || "");
    setZone(p.zone_souhaitee || "");
    if (p.budget_max) setChiffreAffaires(String(p.budget_max));
    if (p.budget_min) setCoutConstruction(String(p.budget_min));
  }

  const T  = toN(coutTerrain);
  const C  = toN(coutConstruction);
  const ca = toN(chiffreAffaires);

  // Honoraires YOMA : 10 % de C par défaut, override manuel possible
  const honorairesAuto = 0.10 * C;
  const honoraires     = honorairesYoma !== "" ? toN(honorairesYoma) : honorairesAuto;

  let acquisition    = 0;
  let foncier        = 0;
  let etudesTaxes    = 0;
  let construction   = C;
  let securiteGest   = 0;
  let financement    = 0;
  let cloture        = 0;
  let reserve        = 0;

  if (pays === "Maroc") {
    const factor = agenceIncluse === "oui" ? 1.091 : 1.066;
    acquisition = factor * T;
    etudesTaxes = 0.06 * C;
    cloture     = 0.02 * C;
    reserve     = 0.05 * C;
  } else {
    foncier     = 1.08 * T;
    etudesTaxes = 0.10 * C;
    securiteGest= 0.03 * C;
    financement = 0.03 * (T + C);
    reserve     = 0.05 * C;
  }

  const terrain = pays === "Maroc" ? acquisition : foncier;
  const frais   = pays === "Maroc"
    ? etudesTaxes + cloture + reserve
    : etudesTaxes + securiteGest + financement + reserve;

  const coutTotal     = terrain + construction + frais + honoraires;
  const benefice      = ca - coutTotal;
  const marge         = ca > 0 ? (benefice / ca) * 100 : 0;
  const roiHonoraires = honoraires > 0 ? ((ca - (terrain + construction + frais)) / honoraires * 100) : 0;

  // Indicateurs spécifiques mode "mission"
  const coutGlobalClient          = construction + frais + honoraires;
  const ratioHonorairesTravaux    = construction > 0     ? (honoraires / construction) * 100   : 0;
  const ratioHonorairesProjet     = coutGlobalClient > 0 ? (honoraires / coutGlobalClient) * 100 : 0;

  function getStatus(m) {
    if (m < 0)   return { label: "Non rentable", color: RED };
    if (m < 10)  return { label: "Faible",       color: "#E67E22" };
    if (m < 20)  return { label: "Correct",      color: "#B7770D" };
    return               { label: "Excellent",    color: GREEN };
  }
  const status = getStatus(marge);

  function ScoreBar({ value, max, color }) {
    const pct = Math.min(100, Math.round((value / max) * 100));
    return (
      <div style={{ height: 8, background: SOFT_BG, borderRadius: 99, overflow: "hidden" }}><div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 99, transition: "width 0.5s" }} /></div>
    );
  }

  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: INK_SOFT }}>Chargement...</div>;
  }

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: INK, maxWidth: 1100 }}><div style={{ marginBottom: 24 }}><div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: GOLD, marginBottom: 4 }}>Décision — Simulation</div><div style={{ fontSize: 24, fontWeight: 700 }}>Simulation Projet</div><div style={{ fontSize: 13, color: INK_SOFT, marginTop: 3 }}>Étude rapide de rentabilité avant lancement</div></div>

      {/* Sélecteur prospect */}
      <div style={{ ...card, background: `linear-gradient(135deg, ${GOLD}15, ${SOFT_BG})`, border: `1px solid ${GOLD}44`, marginBottom: 20 }}><div style={sectionTitle}> Charger un prospect existant</div><div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "end" }}><div><label style={labelStyle}>Sélectionner un prospect</label><select style={inputStyle} value={prospectId} onChange={e => handleSelectProspect(e.target.value)}><option value="">— Choisir un prospect —</option>
              {prospects.map(p => (
                <option key={p.id} value={p.id}>
                  {p.prenom} {p.nom} {p.type_projet ? `· ${p.type_projet}` : ""} {p.zone_souhaitee ? `· ${p.zone_souhaitee}` : ""}
                </option>
              ))}
            </select></div>
          {prospectId && (
            <button onClick={() => { setProspectId(""); setNomClient(""); setTypeProjet(""); setZone(""); setCoutConstruction(""); setChiffreAffaires(""); }}
              style={{ padding: "9px 16px", background: SOFT_BG, color: INK_SOFT, border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 12, cursor: "pointer" }}>
              ✕ Effacer
            </button>
          )}
        </div>
        {prospectId && (
          <div style={{ marginTop: 10, fontSize: 12, color: GREEN, fontWeight: 600 }}>
            ✓ Données pré-remplies depuis la fiche prospect
          </div>
        )}
      </div><div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 20, alignItems: "start" }}><div><div style={card}><div style={sectionTitle}>Informations projet</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}><div><label style={labelStyle}>Nom du client</label><input style={inputStyle} value={nomClient} onChange={e => setNomClient(e.target.value)} placeholder="Ex : M. Hassan El Amrani" /></div><div><label style={labelStyle}>Nom du projet</label><input style={inputStyle} value={nomProjet} onChange={e => setNomProjet(e.target.value)} placeholder="Villa Al Karam..." /></div><div><label style={labelStyle}>Type de projet</label><select style={inputStyle} value={typeProjet} onChange={e => setTypeProjet(e.target.value)}><option value="">— Sélectionner —</option><option value="Villa neuve">Villa neuve</option><option value="Rénovation">Rénovation</option><option value="Riad">Riad</option><option value="Lotissement">Lotissement</option><option value="Immeuble">Immeuble</option></select></div><div><label style={labelStyle}>Type de simulation</label><select style={inputStyle} value={typeSimulation} onChange={e => setTypeSimulation(e.target.value)}><option value="vente">Vente / promotion</option><option value="mission">Mission / suivi chantier</option></select></div><div><label style={labelStyle}>Pays du projet</label><select style={inputStyle} value={pays} onChange={e => setPays(e.target.value)}><option value="Maroc">Maroc</option><option value="France">France</option></select></div><div><label style={labelStyle}>Zone</label><input style={inputStyle} value={zone} onChange={e => setZone(e.target.value)} placeholder="Marrakech, Agadir..." /></div></div></div><div style={card}><div style={sectionTitle}>Structure des coûts (MAD)</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}><div><label style={labelStyle}>Coût terrain</label><input style={inputStyle} type="number" value={coutTerrain} onChange={e => setCoutTerrain(e.target.value)} placeholder="0" /></div><div><label style={labelStyle}>Coût construction</label><input style={inputStyle} type="number" value={coutConstruction} onChange={e => setCoutConstruction(e.target.value)} placeholder="0" /></div><div><label style={labelStyle}>Achat via agence</label><select
                  style={inputStyle}
                  value={agenceIncluse}
                  onChange={e => setAgenceIncluse(e.target.value)}
                ><option value="non">Non</option><option value="oui">Oui</option></select></div><div><label style={labelStyle}>Honoraires YOMA</label><input
                  style={inputStyle}
                  type="number"
                  value={honorairesYoma}
                  onChange={e => setHonorairesYoma(e.target.value)}
                  placeholder="10% du coût construction"
                /></div><div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Budget global client (CA)</label><input style={inputStyle} type="number" value={chiffreAffaires} onChange={e => setChiffreAffaires(e.target.value)} placeholder="Budget total disponible" /></div></div></div></div>

        {/* Résultats */}
        <div><div style={{ ...card, borderTop: `4px solid ${status.color}` }}><div style={sectionTitle}>Résultats simulation</div>

            {typeSimulation === "vente" && (
              <><div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                  {[
                    { label: "Coût total",           val: coutTotal,  color: RED },
                    { label: "Budget client (CA)",   val: ca,         color: GOLD },
                    { label: "Bénéfice prévisionnel",val: benefice,   color: benefice >= 0 ? GREEN : RED },
                    { label: "Honoraires YOMA",      val: honoraires, color: GOLD },
                  ].map(r => (
                    <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", background: SOFT_BG, borderRadius: 8 }}><span style={{ fontSize: 12, color: INK_SOFT }}>{r.label}</span><span style={{ fontSize: 14, fontWeight: 800, color: r.color }}>{formatMoney(r.val)}</span></div>
                  ))}
                </div><div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 13, fontWeight: 700 }}>Marge prévisionnelle</span><span style={{ fontSize: 20, fontWeight: 800, color: status.color }}>{marge.toFixed(1)}%</span></div><ScoreBar value={Math.max(0, marge)} max={30} color={status.color} /><div style={{ marginTop: 16, background: status.color + "15", border: `1px solid ${status.color}40`, borderRadius: 10, padding: "14px 18px", textAlign: "center" }}><div style={{ fontSize: 18, fontWeight: 800, color: status.color }}>{status.label}</div><div style={{ fontSize: 11, color: INK_SOFT, marginTop: 4 }}>
                    {marge < 0    ? "Les coûts dépassent le budget client — revoir l'enveloppe" :
                     marge < 10   ? "Marge insuffisante — négocier ou revoir les coûts" :
                     marge < 20   ? "Projet viable — optimisations possibles" :
                                    "Projet très rentable — lancer la contractualisation"}
                  </div></div></>
            )}

            {typeSimulation === "mission" && (
              <><div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                  {[
                    { label: "Coût construction",              val: construction,         color: RED },
                    { label: "Frais annexes",                  val: frais,               color: GOLD },
                    { label: "Honoraires YOMA",                val: honoraires,          color: GOLD },
                    { label: "Coût global projet client",      val: coutGlobalClient,    color: GREEN },
                  ].map(r => (
                    <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", background: SOFT_BG, borderRadius: 8 }}><span style={{ fontSize: 12, color: INK_SOFT }}>{r.label}</span><span style={{ fontSize: 14, fontWeight: 800, color: r.color }}>{formatMoney(r.val)}</span></div>
                  ))}
                </div><div style={{ marginBottom: 12, display: "flex", flexDirection: "column", gap: 6 }}><div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 13, fontWeight: 700 }}>Ratio honoraires / travaux</span><span style={{ fontSize: 16, fontWeight: 800, color: GOLD }}>{ratioHonorairesTravaux.toFixed(1)}%</span></div><div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 13, fontWeight: 700 }}>Ratio honoraires / projet</span><span style={{ fontSize: 16, fontWeight: 800, color: GOLD }}>{ratioHonorairesProjet.toFixed(1)}%</span></div></div></>
            )}
          </div><div style={card}><div style={sectionTitle}>Décomposition des coûts</div>
            {coutTotal > 0 && [
              { label: "Terrain",      val: terrain,      total: coutTotal },
              { label: "Construction", val: construction, total: coutTotal },
              { label: "Frais divers", val: frais,        total: coutTotal },
              { label: "Honoraires",   val: honoraires,   total: coutTotal },
            ].map(r => {
              const pct = coutTotal > 0 ? Math.round((r.val / coutTotal) * 100) : 0;
              return (
                <div key={r.label} style={{ marginBottom: 10 }}><div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span style={{ fontSize: 12, color: INK_SOFT }}>{r.label}</span><span style={{ fontSize: 12, fontWeight: 700, color: INK }}>{pct}% — {formatMoney(r.val)}</span></div><ScoreBar value={r.val} max={coutTotal} color={GOLD} /></div>
              );
            })}
            {coutTotal === 0 && <div style={{ fontSize: 13, color: "#bbb", fontStyle: "italic" }}>Saisissez les coûts pour voir la décomposition</div>}
          </div></div></div></div>
  );
}